import type { ApiDocument, ApiFolder } from "@workith/shared";
import { FileText } from "lucide-react";
import type { ReactNode } from "react";
import { DocumentActions } from "./DocumentActions";
import { formatDocumentDate } from "./documentDisplay";

interface DocumentRowProps {
  document: ApiDocument;
  folders?: ApiFolder[];
  onOpenDocument: (document: ApiDocument) => void;
  renderBadge: (document: ApiDocument) => ReactNode;
  renderDescription?: (document: ApiDocument) => ReactNode;
  onDocumentUpdate?: () => void;
  rowTestId: string;
  mobileBadgeTestId: string;
}

export function DocumentRow({
  document,
  folders = [],
  onOpenDocument,
  renderBadge,
  renderDescription,
  onDocumentUpdate,
  rowTestId,
  mobileBadgeTestId,
}: DocumentRowProps) {
  const description = renderDescription?.(document);

  return (
    <div
      className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b px-4 py-3 last:border-b-0 md:grid-cols-[minmax(0,1fr)_140px_120px_40px]"
      data-testid={rowTestId}
    >
      <button
        type="button"
        className="flex min-w-0 items-center gap-3 text-left"
        onClick={() => onOpenDocument(document)}
        aria-label={`Open ${document.name}`}
      >
        <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
        <span className="min-w-0">
          <span className="block truncate font-medium">{document.name}</span>
          {description ? (
            <span className="block truncate text-xs text-muted-foreground">{description}</span>
          ) : null}
          <span className="mt-1 block text-xs text-muted-foreground md:hidden">
            {formatDocumentDate(document.updated_at)}
          </span>
          <span className="mt-2 flex md:hidden" data-testid={mobileBadgeTestId}>
            {renderBadge(document)}
          </span>
        </span>
      </button>

      <div className="hidden items-center md:flex">{renderBadge(document)}</div>
      <span className="hidden truncate text-sm text-muted-foreground md:block">
        {formatDocumentDate(document.updated_at)}
      </span>
      <div className="flex justify-end">
        {onDocumentUpdate ? (
          <DocumentActions document={document} folders={folders} onUpdate={onDocumentUpdate} />
        ) : null}
      </div>
    </div>
  );
}
